// @ts-nocheck
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import Button from "@insite/mobius/Button/Button";
import Checkbox from "@insite/mobius/Checkbox";
import TextField, { TextFieldPresentationProps } from "@store/MobiusOverrides/TextField/TextField";

import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { SignatureModel } from "@store/Framework/Store/Bunzl/Pages/Signature/SignatureReducer";
import { textFieldVariantStyles } from "@store/Library/Components/Bunzl/StepFormComponents/StepFormTextField";
import SigningModal from "@store/Library/Components/Signature/SigningModal";
import { TermsSignatureData } from "@store/Library/Widgets/CreditForm/CreditFormForm";
import SignatureIcon from "@store/Library/Widgets/SignatureIntegration/SignatureIcon";

import parse from "html-react-parser";
import React, { useContext } from "react";
import styled, { css } from "styled-components";
import { CreditFormContext } from "../../Widgets/CreditForm/CreditFormContextContainer";

interface CreditFormTermsAndConditionsStyles {
    container?: InjectableCss;
    textFieldProps?: TextFieldPresentationProps;
    fieldWrapper?: InjectableCss;
}
const StyledButton = styled(Button)`
    display: flex;
    column-gap: 0.5rem;
    margin-block: 1rem;
    width: fit-content;
`;
const styles: CreditFormTermsAndConditionsStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 1rem;
            padding: 2rem;
        `,
    },
    fieldWrapper: {
        css: css`
            display: flex;
            gap: 1.25rem;
            flex-wrap: wrap;
        `,
    },
    textFieldProps: textFieldVariantStyles.medium,
};
interface Props {
    setSignatureData: React.Dispatch<React.SetStateAction<TermsSignatureData | undefined>>;
}

export interface TermsAndConditionsState {
    signatureState: SignatureModel;
    firstName: string;
    lastName: string;
    position: string;
    email: string;
    emailError: string | undefined;
    authorized: boolean;
    agreedToTerms: boolean;
}
const initialTermsAndConditionsState: TermsAndConditionsState = {
    signatureState: {
        customerName: "",
        customerSignature: "",
        timestamp: "",
    },
    agreedToTerms: false,
    firstName: "",
    emailError: undefined,
    lastName: "",
    position: "",
    email: "",
    authorized: false,
};
type TermsAndConditionsAction =
    | {
          type: "SIGNATURE";
          payload: SignatureModel;
      }
    | {
          type: "FIRST_NAME";
          payload: string;
      }
    | {
          type: "LAST_NAME";
          payload: string;
      }
    | {
          type: "POSITION";
          payload: string;
      }
    | {
          type: "EMAIL";
          payload: string;
      }
    | {
          type: "AUTHORIZED";
          payload: boolean;
      }
    | {
          type: "AGREED_TO_TERMS";
          payload: boolean;
      };

const areTermsValid = (state: TermsAndConditionsState): boolean => {
    const { signatureState, firstName, lastName, position, email, authorized, agreedToTerms } = state;
    if (signatureState.customerSignature && firstName && lastName && position && email && authorized && agreedToTerms) {
        return true;
    }
    return false;
};
const termsAndConditionReducer = (
    setter: React.Dispatch<React.SetStateAction<TermsSignatureData | undefined>>,
    state: TermsAndConditionsState,
    action: TermsAndConditionsAction,
): TermsAndConditionsState => {
    let newState: TermsAndConditionsState = { ...state };
    switch (action.type) {
        case "SIGNATURE":
            newState = {
                ...newState,
                signatureState: { ...action.payload, customerName: `${state.firstName} ${state.lastName}` },
            };
            break;
        case "FIRST_NAME":
            newState = {
                ...state,
                firstName: action.payload,
                signatureState: { ...newState.signatureState, customerName: `${action.payload} ${state.lastName}` },
            };
            break;
        case "LAST_NAME":
            newState = {
                ...state,
                lastName: action.payload,
                signatureState: {
                    ...newState.signatureState,
                    customerName: `${state.firstName} ${action.payload}`,
                },
            };
            break;
        case "POSITION":
            newState = { ...newState, position: action.payload };
            break;
        case "EMAIL":
            newState = { ...newState, email: action.payload };
            newState.emailError = validateEmail(action.payload) ? undefined : "Invalid email";
            break;
        case "AUTHORIZED":
            newState = { ...newState, authorized: action.payload };
            break;
        case "AGREED_TO_TERMS":
            newState = { ...newState, agreedToTerms: action.payload };
            break;
    }
    if (areTermsValid(newState)) {
        setter({
            firstName: newState.firstName,
            lastName: newState.lastName,
            position: newState.position,
            signature: newState.signatureState.customerSignature,
            signedAt: newState.signatureState.timestamp,
            email: newState.email,
        });
    }

    return newState;
};
const CreditFormTermsAndConditions: React.FC<Props> = ({ setSignatureData }) => {
    const {
        storeKey = "",
        termsAndConditionsCreditAccount = "",
        termsAndConditionsTermAccount = "",
    } = useContext(CreditFormContext) ?? {};
    const accountType = useAppSelector(
        state => state.bunzl.pages.stepForm?.[storeKey].steps[0]?.inputs?.accountType?.value,
    );
    const termsAndConditions =
        accountType === siteMessage("CreditForm_TermAccount")
            ? termsAndConditionsTermAccount
            : termsAndConditionsCreditAccount;

    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [{ authorized, email, firstName, lastName, position, signatureState, agreedToTerms, emailError }, dispatch] =
        React.useReducer(
            (state, action) => termsAndConditionReducer(setSignatureData, state, action),
            initialTermsAndConditionsState,
        );

    const showSignatureButton = firstName && lastName && position && email && authorized && agreedToTerms;
    return (
        <StyledWrapper {...styles.container}>
            {parse(termsAndConditions, parserOptions)}
            <Checkbox
                checked={agreedToTerms}
                onChange={(_, value) => dispatch({ type: "AGREED_TO_TERMS", payload: value })}
            >
                {translate("I have read and agree to the terms and conditions")}
            </Checkbox>
            {agreedToTerms && (
                <Checkbox
                    checked={authorized}
                    onChange={(_, value) => dispatch({ type: "AUTHORIZED", payload: value })}
                >
                    {translate("I am authorized to sign this document")}
                </Checkbox>
            )}
            {authorized && (
                <StyledWrapper {...styles.fieldWrapper}>
                    <TextField
                        required
                        {...styles.textFieldProps}
                        label={translate("First Name")}
                        value={firstName}
                        autoComplete="given-name"
                        onChange={e => dispatch({ type: "FIRST_NAME", payload: e.target.value })}
                    />
                    <TextField
                        required
                        autoComplete="family-name"
                        {...styles.textFieldProps}
                        value={lastName}
                        label={translate("Last Name")}
                        onChange={e => dispatch({ type: "LAST_NAME", payload: e.target.value })}
                    />
                    <TextField
                        required
                        autoComplete="email"
                        {...styles.textFieldProps}
                        label={translate("Email")}
                        error={showSignatureButton ? emailError : undefined}
                        value={email}
                        onChange={e => dispatch({ type: "EMAIL", payload: e.target.value })}
                    />
                    <TextField
                        required
                        {...styles.textFieldProps}
                        label={translate("Position")}
                        value={position}
                        onChange={e => dispatch({ type: "POSITION", payload: e.target.value })}
                    />
                </StyledWrapper>
            )}
            {showSignatureButton && (
                <StyledButton disabled={!!emailError} sizeVariant="medium" onClick={() => setIsModalOpen(true)}>
                    <SignatureIcon /> <Typography>{translate("Please Sign Here")}</Typography>
                </StyledButton>
            )}
            <SigningModal
                hideEmail={true}
                acknowledgeText={translate("acknowledge that I have read and agree to the terms and conditions")}
                setSignatureState={signatureState => dispatch({ type: "SIGNATURE", payload: signatureState })}
                signatureData={signatureState}
                editable
                editableName={false}
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
            />
        </StyledWrapper>
    );
};

export default CreditFormTermsAndConditions;
